import React, { useMemo, useState } from "react";
import { Button, Card, Checkbox, Dropdown, Input, Menu, Space, Table, Tooltip } from "antd";
import { DownOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useProductAvailability } from "../../../../../data/hooks/firestore/useProductAvailability";
import utils from "utils";
import dayjs from "dayjs";
import styled from "styled-components";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const ProductsAvailability = ({
  orders = [],
  dateRange,
  dateRangeText,
  loading: parentLoading,
  companyFilter = "All",
}) => {
  const { t } = useTranslation();
  const { products } = useProducts();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Availability filters - all enabled by default
  const [filters, setFilters] = useState({
    productInStock: true,
    productInSeason: true,
    isVisible: true,
    stockLevel: true,
  });

  // Product group filters - all enabled by default
  const [selectedProductGroups, setSelectedProductGroups] = useState([]);

  // Search state
  const [searchText, setSearchText] = useState("");

  // Use the availability hook
  const {
    loading: availabilityLoading,
    getProductTimeline,
    getUnavailablePeriods,
    getUnavailabilityReasons,
    calculateFilteredAvailability,
  } = useProductAvailability(dateRange, products);

  const loading = parentLoading || availabilityLoading;

  // Calculate date range in milliseconds
  const startTimestamp = dateRange ? dayjs(dateRange[0]).tz("Europe/Amsterdam").startOf("day").unix() : 0;
  const endTimestamp = dateRange ? dayjs(dateRange[1]).tz("Europe/Amsterdam").endOf("day").unix() : 0;
  const totalDuration = endTimestamp - startTimestamp;

  // Build timeline segments for a product
  const buildTimelineSegments = (productId) => {
    const timeline = getProductTimeline(productId);
    if (timeline.length === 0) return [];

    const segments = [];

    for (let i = 0; i < timeline.length; i++) {
      const currentStatus = timeline[i];
      const nextStatus = timeline[i + 1];

      const segmentStart = Math.max(currentStatus.timestamp, startTimestamp);
      const segmentEnd = nextStatus ? nextStatus.timestamp : endTimestamp;

      // Skip if segment is outside date range
      if (segmentEnd < startTimestamp || segmentStart > endTimestamp) {
        continue;
      }

      const available = calculateFilteredAvailability(currentStatus, filters);
      const reasons = available ? [] : getUnavailabilityReasons(currentStatus, filters);

      segments.push({
        startTimestamp: segmentStart,
        endTimestamp: segmentEnd,
        available,
        reasons,
      });
    }

    return segments;
  };

  // Render timeline visualization
  const renderTimeline = (productId) => {
    const segments = buildTimelineSegments(productId);

    if (segments.length === 0) {
      return <div style={{ padding: "8px", color: "#999" }}>{t("no_availability_data")}</div>;
    }

    return (
      <TimelineContainer
        onClick={() => {
          if (expandedRowKeys.includes(productId)) {
            setExpandedRowKeys(expandedRowKeys.filter((key) => key !== productId));
          } else {
            setExpandedRowKeys([...expandedRowKeys, productId]);
          }
        }}
      >
        {segments.map((segment, index) => {
          const startPercent = ((segment.startTimestamp - startTimestamp) / totalDuration) * 100;
          const duration = segment.endTimestamp - segment.startTimestamp;
          const widthPercent = (duration / totalDuration) * 100;

          const tooltipContent = (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {dayjs.unix(segment.startTimestamp).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm")}
                {" → "}
                {dayjs.unix(segment.endTimestamp).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm")}
              </div>
              <div>
                {t("status")}: {segment.available ? t("available") : t("unavailable")}
              </div>
              {!segment.available && segment.reasons.length > 0 && (
                <div>
                  {t("reasons")}: {segment.reasons.map((reason) => t(reason)).join(", ")}
                </div>
              )}
            </div>
          );

          return (
            <Tooltip key={index} title={tooltipContent} placement="top">
              <TimelineSegment
                $available={segment.available}
                style={{
                  left: `${startPercent}%`,
                  width: `${widthPercent}%`,
                }}
              />
            </Tooltip>
          );
        })}
      </TimelineContainer>
    );
  };

  // Render expanded row content with unavailable periods
  const renderExpandedRow = (record) => {
    const unavailablePeriods = getUnavailablePeriods(record.productId, filters);

    if (unavailablePeriods.length === 0) {
      return <div style={{ padding: "16px", color: "#52c41a" }}>✔︎ {t("product_was_available_entire_period")}</div>;
    }

    return (
      <div style={{ padding: "16px" }}>
        <h4 style={{ fontWeight: 600, fontSize: 16 }}>{t("unavailable_periods")}:</h4>
        {unavailablePeriods.map((period, index) => (
          <UnavailablePeriodItem key={index}>
            <div className="period-time">
              {dayjs.unix(period.startTimestamp).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm")}
              {" - "}
              {dayjs.unix(period.endTimestamp).tz("Europe/Amsterdam").format("DD-MM-YYYY HH:mm")}
            </div>
            <div className="period-reasons">
              {t("reasons")}: {period.reasons.map((reason) => t(reason)).join(", ")}
            </div>
          </UnavailablePeriodItem>
        ))}
      </div>
    );
  };

  // Get unique product groups from products
  const allProductGroups = useMemo(() => {
    const groupSet = new Set();
    products.forEach((product) => {
      const group = product.productGroup;
      if (group && group !== "null" && group !== null && group !== undefined && group !== "") {
        groupSet.add(group);
      }
    });
    return Array.from(groupSet).sort();
  }, [products]);

  // Prepare table data with filtering
  const tableData = useMemo(() => {
    let filteredProducts = products.map((product) => ({
      productId: product.id || product.productId,
      productName: product.productName || product.productNameNL,
      productCategory: product.category?.en || product.productCategory,
      productGroup: product.productGroup,
      company: product.company,
    }));

    // Apply company filter
    if (companyFilter !== "All") {
      filteredProducts = filteredProducts.filter((product) => product.company === companyFilter);
    }

    // Apply product group filter
    if (selectedProductGroups.length > 0) {
      filteredProducts = filteredProducts.filter((product) => selectedProductGroups.includes(product.productGroup));
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filteredProducts = filteredProducts.filter(
        (product) =>
          product.productName?.toLowerCase().includes(searchLower) ||
          product.productId?.toLowerCase().includes(searchLower),
      );
    }

    return filteredProducts;
  }, [products, selectedProductGroups, searchText, companyFilter]);

  // Table columns
  const columns = [
    {
      title: t("product_name"),
      dataIndex: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      width: 250,
      defaultSortOrder: "descend",
      render: (text, record) => (
        <button
          className={"text-button"}
          onClick={() => {
            if (expandedRowKeys.includes(record.productId)) {
              setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.productId));
            } else {
              setExpandedRowKeys([...expandedRowKeys, record.productId]);
            }
          }}
        >
          {text}
        </button>
      ),
    },
    {
      title: t("availability_timeline"),
      key: "timeline",
      render: (_, record) => renderTimeline(record.productId),
    },
  ];

  const handleFilterChange = (filterKey) => {
    setFilters({
      ...filters,
      [filterKey]: !filters[filterKey],
    });
  };

  const handleProductGroupToggle = (group) => {
    setSelectedProductGroups((prev) => {
      if (prev.includes(group)) {
        return prev.filter((g) => g !== group);
      } else {
        return [...prev, group];
      }
    });
  };

  const handleSelectAllProductGroups = () => {
    if (selectedProductGroups.length === allProductGroups.length) {
      setSelectedProductGroups([]);
    } else {
      setSelectedProductGroups([...allProductGroups]);
    }
  };

  // Availability filters dropdown menu
  const availabilityMenu = (
    <Menu>
      <Menu.Item key="productInStock" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={filters.productInStock} onChange={() => handleFilterChange("productInStock")}>
          {t("in_stock")}
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="productInSeason" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={filters.productInSeason} onChange={() => handleFilterChange("productInSeason")}>
          {t("in_season")}
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="isVisible" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={filters.isVisible} onChange={() => handleFilterChange("isVisible")}>
          {t("visible")}
        </Checkbox>
      </Menu.Item>
      <Menu.Item key="stockLevel" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={filters.stockLevel} onChange={() => handleFilterChange("stockLevel")}>
          {t("stock_available")}
        </Checkbox>
      </Menu.Item>
    </Menu>
  );

  // Product group filters dropdown menu
  const productGroupMenu = (
    <Menu>
      <Menu.Item key="all" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selectedProductGroups.length === allProductGroups.length}
          indeterminate={selectedProductGroups.length > 0 && selectedProductGroups.length < allProductGroups.length}
          onChange={handleSelectAllProductGroups}
        >
          {t("all_groups")}
        </Checkbox>
      </Menu.Item>
      <Menu.Divider />
      {allProductGroups.map((group) => (
        <Menu.Item key={group} onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selectedProductGroups.includes(group)} onChange={() => handleProductGroupToggle(group)}>
            {t(group.toLowerCase())}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <span className="text-muted">{dateRangeText}</span>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Space size="middle" wrap>
          <Input
            placeholder={t("search_product")}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />

          <Dropdown overlay={availabilityMenu} trigger={["click"]}>
            <Button>
              <FilterOutlined /> {t("availability_filters")} <DownOutlined />
            </Button>
          </Dropdown>

          <Dropdown overlay={productGroupMenu} trigger={["click"]}>
            <Button>
              <FilterOutlined /> {t("product_group")}
              {selectedProductGroups.length > 0 && selectedProductGroups.length < allProductGroups.length && (
                <span style={{ marginLeft: 4 }}>({selectedProductGroups.length})</span>
              )}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "#52c41a",
                border: "1px solid #d9d9d9",
              }}
            />
            <span>{t("available")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "#ff4d4f",
                border: "1px solid #d9d9d9",
              }}
            />
            <span>{t("unavailable")}</span>
          </div>
        </Space>
      </div>

      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={tableData}
          rowKey="productId"
          loading={loading}
          pagination={{ showSizeChanger: true, pageSize: 50 }}
          expandable={{
            expandedRowRender: renderExpandedRow,
            expandedRowKeys: expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
          }}
          scroll={{ x: 800 }}
        />
      </div>
    </Card>
  );
};

const TimelineContainer = styled.div`
  width: 100%;
  height: 40px;
  position: relative;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
`;

const TimelineSegment = styled.div`
  position: absolute;
  height: 100%;
  top: 0;
  background-color: ${(props) => (props.$available ? "#52c41a" : "#ff4d4f")};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const UnavailablePeriodItem = styled.div`
  padding: 12px;
  margin-bottom: 8px;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;

  .period-time {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .period-reasons {
    color: #cf1322;
  }
`;

export default ProductsAvailability;
